import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, FormikProvider, useFormik, FieldArray } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  MenuItem,
  Stack,
  IconButton,
  Card,
  CardContent,
  Autocomplete,
  Chip,
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useSnackbar } from 'notistack';
import { Icon } from '@iconify/react';
import { updateNonCartStudentData } from '../../../../../redux/features/CRDslice';
import dayjs from 'dayjs';
import { holdPaymentUpload } from '../../../../../redux/features/generalSlice';

const FormSection = ({ setOpen }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { loading, nonCartStudentData, countryCodeAndCountry } = useSelector((state) => state.crd);

  const student = nonCartStudentData?.student_details || {};
  const incomeList = nonCartStudentData?.income_details || [];
  const remarksList = nonCartStudentData?.cro_assignment_details || {};

  const validationSchema = Yup.object({
    student_name: Yup.string().required('Student name is required'),
    phone_number: Yup.string().required('Phone number is required'),
    income_details: Yup.array().of(
      Yup.object().shape({
        income_amount: Yup.number().required('Amount required'),
        income_transaction_date: Yup.string().required('Date required'),
        payment_account: Yup.number().required('Account required'),
        payment_mode: Yup.string().required('Mode required'),
        fee_type: Yup.string().required('Fee type required'),
        income_remarks: Yup.string().nullable(),
      }),
    ),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      student_name: student.student_name || '',
      dob: student.dob || '',
      phone_number: student.student_mob_no || '',
      student_country_code: student.student_country_code || '',
      gender: student.gender,
      a_phone: student.a_phone || '',
      sro_remarks_total_fees: remarksList.sro_remarks_total_fees || '',
      sro_remarks_student_details: remarksList.sro_remarks_student_details || '',
      sro_remarks_subjects_sessions: remarksList.sro_remarks_subjects_sessions || '',
      crm_remarks: remarksList.crm_remarks || '',
      income_details:
        incomeList.length > 0
          ? incomeList.map((item) => ({
              income_id: item.income_id,
              income_amount: item.income_amount,
              income_transaction_date: item.income_transaction_date,
              payment_account: item.payment_account,
              payment_mode: item.payment_mode,
              fee_type: item.fee_type,
              income_remarks: item.income_remarks,
              income_screenshot: item.income_screenshot || null,
              userID: item.userID || null,
            }))
          : [
              {
                income_amount: '',
                income_transaction_date: '',
                payment_account: '',
                payment_mode: '',
                fee_type: '',
                income_remarks: '',
                income_screenshot: null,
                userID: '',
              },
            ],
    },
    validationSchema,
    onSubmit: (values) => {
      const courseDetails = nonCartStudentData?.course_enrollment_details || {};

      const updatedIncome = values.income_details.map((item) => {
        const transactionDate = item.income_transaction_date ? dayjs(item.income_transaction_date) : null;

        return {
          ...item,
          income_amount: item.income_amount ? Number(item.income_amount) : 0,
          income_year: transactionDate ? transactionDate.year() : null,
          income_month: transactionDate ? transactionDate.month() + 1 : null,
          userID: item.userID || '',
          delete_status: item.delete_status || 0,
        };
      });

      const updatedData = {
        ...nonCartStudentData,
        student_details: {
          ...student,
          student_name: values.student_name,
          dob: values.dob,
          student_mob_no: values.phone_number,
          student_country_code: values.student_country_code,
          gender: values.gender,
          a_phone: values.a_phone,
        },
        cro_assignment_details: {
          ...remarksList,
          sro_remarks_total_fees: values.sro_remarks_total_fees,
          sro_remarks_student_details: values.sro_remarks_student_details,
          sro_remarks_subjects_sessions: values.sro_remarks_subjects_sessions,
          crm_remarks: values.crm_remarks,
        },
        income_details: updatedIncome,
        course_enrollment_id: courseDetails.course_enrollment_id,
      };

      dispatch(updateNonCartStudentData(updatedData)).then((res) => {
        if (res.payload?.status === 'success') {
          enqueueSnackbar(res.payload.message, { variant: 'success' });
          setOpen(false);
        } else {
          enqueueSnackbar(res.payload.message, { variant: 'error' });
        }
      });
    },
  });

  const { values, handleChange, setFieldValue } = formik;

  const uploadFile = async (file) => {
    try {
      const response = await dispatch(holdPaymentUpload(file));

      const filename = response?.payload?.data?.filename;
      if (filename) {
        return filename;
      } else {
        enqueueSnackbar('File upload failed: no filename returned', { variant: 'error' });
        return '';
      }
    } catch (error) {
      console.error('File upload failed:', error);
      enqueueSnackbar('File upload failed', { variant: 'error' });
      return '';
    }
  };

  const handleFileChange = async (e, index) => {
    const file = e.target.files[0];
    if (file) {
      const filename = await uploadFile(file);
      if (filename) {
        setFieldValue(`income_details[${index}].income_screenshot`, filename);
        enqueueSnackbar('File uploaded successfully', { variant: 'success' });
      } else {
        enqueueSnackbar('Upload failed, please try again', { variant: 'error' });
      }
    }
  };

  return (
    <FormikProvider value={formik}>
      <Form onSubmit={formik.handleSubmit}>
        <Box p={2}>
          <Typography variant="h6" mb={2}>
            Student Details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Student Name"
                name="student_name"
                value={values.student_name}
                onChange={handleChange}
                size="small"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Date of Birth"
                  value={values.dob ? dayjs(values.dob) : null}
                  onChange={(val) => setFieldValue('dob', val ? dayjs(val).format('YYYY-MM-DD') : '')}
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Phone Number"
                name="phone_number"
                value={values.phone_number}
                onChange={handleChange}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Autocomplete
                disabled={loading}
                name="student_country_code"
                id="student_country_code"
                size="small"
                options={countryCodeAndCountry}
                getOptionLabel={(option) => option.label || ''}
                value={
                  countryCodeAndCountry?.find((CNTRY) => CNTRY.phone_code == formik.values.student_country_code) || null
                }
                onChange={handleChange}
                renderInput={(params) => (
                  <TextField disabled fullWidth autoComplete="off" {...params} label="Student Country Code" />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                label="Gender"
                name={`gender`}
                value={formik.values.gender}
                onChange={handleChange}
                size="small"
              >
                <MenuItem value="1">Male</MenuItem>
                <MenuItem value="2">Female</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Parent Phone Number"
                name="a_phone"
                value={values.a_phone}
                onChange={handleChange}
                size="small"
              />
            </Grid>
          </Grid>

          <Box mt={4}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Income Details</Typography>
            </Stack>

            <FieldArray
              name="income_details"
              render={(arrayHelpers) => (
                <>
                  {values.income_details
                    .filter((item) => item.delete_status !== 1)
                    .map((item, index) => (
                      <Card
                        key={index}
                        sx={{
                          mb: 3,
                          boxShadow: ' rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 2px 6px 2px;',
                        }}
                      >
                        <CardContent>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                            <Chip
                              variant="filled"
                              size="small"
                              icon={<Icon icon={'game-icons:money-stack'} />}
                              color="secondary"
                              label={`Income : ${index + 1}`}
                            />
                            {values.income_details.length > 1 && (
                              <IconButton
                                color="error"
                                onClick={() => {
                                  const income = values.income_details[index];

                                  if (income.income_id) {
                                    const updatedIncomes = values.income_details.map((item, idx) =>
                                      idx === index ? { ...item, delete_status: 1 } : item,
                                    );
                                    const visibleIncomes = updatedIncomes.filter((item) => item.delete_status !== 1);
                                    setFieldValue(
                                      'income_details',
                                      visibleIncomes.concat(
                                        updatedIncomes.filter((item) => item.delete_status === 1), // push deleted to end (hidden)
                                      ),
                                    );
                                  } else {
                                    arrayHelpers.remove(index);
                                  }
                                }}
                              >
                                <Icon icon="mdi:delete" />
                              </IconButton>
                            )}
                          </Stack>

                          <Grid container spacing={2}>
                            <Grid item xs={12} md={3}>
                              <TextField
                                fullWidth
                                label="Amount"
                                type="number"
                                name={`income_details[${index}].income_amount`}
                                value={item.income_amount}
                                onChange={handleChange}
                                size="small"
                              />
                            </Grid>

                            <Grid item xs={12} md={3}>
                              <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                  label="Transaction Date"
                                  value={item.income_transaction_date ? dayjs(item.income_transaction_date) : null}
                                  onChange={(val) =>
                                    setFieldValue(
                                      `income_details[${index}].income_transaction_date`,
                                      val ? dayjs(val).format('YYYY-MM-DD') : '',
                                    )
                                  }
                                  slotProps={{
                                    textField: { fullWidth: true, size: 'small' },
                                  }}
                                />
                              </LocalizationProvider>
                            </Grid>

                            <Grid item xs={12} md={2}>
                              <TextField
                                select
                                fullWidth
                                label="Account"
                                name={`income_details[${index}].payment_account`}
                                value={item.payment_account}
                                onChange={handleChange}
                                size="small"
                              >
                                <MenuItem value={1}>Indian A/c</MenuItem>
                                <MenuItem value={2}>GCC A/c</MenuItem>
                              </TextField>
                            </Grid>

                            <Grid item xs={12} md={2}>
                              <TextField
                                select
                                fullWidth
                                label="Payment Mode"
                                name={`income_details[${index}].payment_mode`}
                                value={item.payment_mode}
                                onChange={handleChange}
                                size="small"
                              >
                                <MenuItem value="Bank Transfer">Bank Transfer</MenuItem>
                                <MenuItem value="Cash">Cash</MenuItem>
                                <MenuItem value="UPI">UPI</MenuItem>
                                <MenuItem value="EMI">EMI</MenuItem>
                              </TextField>
                            </Grid>

                            <Grid item xs={12} md={2}>
                              <TextField
                                select
                                fullWidth
                                label="Fee Type"
                                name={`income_details[${index}].fee_type`}
                                value={item.fee_type}
                                onChange={handleChange}
                                size="small"
                              >
                                <MenuItem value="Tuition Fee">Tuition Fee</MenuItem>
                                <MenuItem value="Admission Fee">Admission Fee</MenuItem>
                                <MenuItem value="EMI Processing Fee">EMI Processing Fee</MenuItem>
                              </TextField>
                            </Grid>

                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                multiline
                                rows={2}
                                label="Remarks"
                                name={`income_details[${index}].income_remarks`}
                                value={item.income_remarks}
                                onChange={handleChange}
                                size="small"
                              />
                            </Grid>
                            {!item.income_id && (
                              <Grid item xs={12} sm={12}>
                                <TextField
                                  select
                                  fullWidth
                                  label="Assign User"
                                  name={`income_details[${index}].userID`}
                                  value={item.userID || ''}
                                  onChange={handleChange}
                                  size="small"
                                >
                                  <MenuItem value={nonCartStudentData?.course_enrollment_details?.cro_id}>
                                    {nonCartStudentData?.course_enrollment_details?.cro_name || 'CRO'}
                                  </MenuItem>
                                  <MenuItem value={nonCartStudentData?.course_enrollment_details?.sro_id}>
                                    {nonCartStudentData?.course_enrollment_details?.sro_name || 'OGA'}
                                  </MenuItem>
                                </TextField>
                              </Grid>
                            )}
                            <Grid item xs={12} md={6}>
                              <Button variant="outlined" component="label" fullWidth size="small">
                                Upload Screenshot
                                <input
                                  type="file"
                                  hidden
                                  accept="image/*"
                                  onChange={(e) => handleFileChange(e, index)}
                                />
                              </Button>
                            </Grid>

                            <Grid item xs={12} md={6}>
                              {item.income_screenshot?.match(/\.(jpeg|jpg|png|gif)$/i) && (
                                <Box mt={1} sx={{ display: 'flex', justifyContent: 'center' }}>
                                  <img
                                    src={`${process.env.REACT_APP_UPLOADS_URL}${item.income_screenshot}`}
                                    alt="Screenshot"
                                    style={{
                                      width: '120px',
                                      borderRadius: '6px',
                                    }}
                                  />
                                </Box>
                              )}
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    ))}
                </>
              )}
            />
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={() =>
                setFieldValue('income_details', [
                  ...values.income_details,
                  {
                    income_amount: null,
                    income_transaction_date: '',
                    payment_account: '',
                    payment_mode: '',
                    fee_type: '',
                    income_remarks: '',
                    income_screenshot: null,
                  },
                ])
              }
            >
              + Add Income
            </Button>
          </Box>
          <Box>
            <Typography variant="h6" mt={4} mb={2}>
              Remarks
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="CRM Remarks"
                  name="crm_remarks"
                  value={values.crm_remarks}
                  onChange={handleChange}
                  size="small"
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Total Fees"
                  name="sro_remarks_total_fees"
                  value={values.sro_remarks_total_fees}
                  onChange={handleChange}
                  size="small"
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Student Details Remarks"
                  name="sro_remarks_student_details"
                  value={values.sro_remarks_student_details}
                  onChange={handleChange}
                  size="small"
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Subjects & Sessions Remarks"
                  name="sro_remarks_subjects_sessions"
                  value={values.sro_remarks_subjects_sessions}
                  onChange={handleChange}
                  size="small"
                  margin="normal"
                />
              </Grid>
            </Grid>
          </Box>
          <Box mt={4} textAlign="right">
            <Button type="submit" variant="contained" color="primary" disabled={loading}>
              {loading ? 'Updating...' : 'Update Details'}
            </Button>
          </Box>
        </Box>
      </Form>
    </FormikProvider>
  );
};

export default FormSection;
